<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Brindes</h5>
      <b-button
        v-if="hasPermission('adicionar_brindes')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Novo Brinde"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por nome"
        class="form-control"
        @input="filtrarBrindes"
      />
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('nome')">
              Nome {{ arrow('nome') }}
            </button>
          </th>
          <th>
            Descrição
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('ativo')">
              Status {{ arrow('ativo') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in brindes" :key="row.id">
          <td>{{ row.nome }}</td>
          <td>{{ row.descricao }}</td>
          <td>{{ row.ativo ? 'Ativo' : 'Inativo' }}</td>
          <td class="text-end">
            <router-link
              v-if="hasPermission('detalhar_brindes')"
              :to="`/cadastros/brindes/${row.id}/detalhar`"
            >
              <i class="bi bi-eye-fill text-primary mx-2" title="Detalhar"></i>
            </router-link>
            <router-link
              v-if="hasPermission('alterar_brindes')"
              :to="`/cadastros/brindes/${row.id}`"
            >
              <i class="bi bi-pencil-fill text-warning mx-2" title="Editar"></i>
            </router-link>
            <i
              v-if="hasPermission('excluir_brindes')"
              class="bi bi-trash-fill text-danger mx-2"
              @click="excluirBrinde(row.id)"
              title="Excluir"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- 1. Resumo -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <!-- 2. Seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limitePorPagina"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="onChangeLimit"
        />
      </div>

      <!-- 3. Controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, getCurrentInstance, computed, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'

const router = useRouter()
const { proxy } = getCurrentInstance()

// Destrutura apenas o que efetivamente usamos
const {
  items: brindes,
  totalItems,
  page: paginaAtual,
  limit: limitePorPagina,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/brindes`,
  { key: 'nome', order: 'asc' }
)

// Opções de limite de página
const pageOptions = ref([10, 15, 20, 25, 50])

// Computeds para "Mostrando X a Y de Z"
const startRow = computed(() =>
  (paginaAtual.value - 1) * limitePorPagina.value + 1
)
const endRow = computed(() =>
  Math.min(paginaAtual.value * limitePorPagina.value, totalItems.value)
)

// Watchers: recarrega ao mudar página ou limite

watch(paginaAtual, () => {
  load()
})

// Inicialização
onMounted(() => {
  filters.value = { nome: '' }
  load()
})

// Handlers de filtro
function filtrarBrindes() {
  paginaAtual.value = 1
  load()
}
function limparFiltros() {
  filters.value.nome = ''
  paginaAtual.value = 1
  load()
}
function onChangeLimit() {
  paginaAtual.value = 1
  load()
}
function onPageChange() {
  load()
}

// Rotas e exclusão
function irParaCadastro() {
  router.push('/cadastros/brindes/novo')
}
async function excluirBrinde(id) {
  const confirmou = await proxy.$confirm(
    'Tem certeza que deseja excluir este brinde?',
    'Confirmação'
  )
  if (!confirmou) return
  try {
    await axios.delete(`/cadastros/brindes/${id}`)
    load()
    proxy.$toast.success('Brinde excluído com sucesso!')
  } catch (e) {
    const msg = e.response?.data?.error || 'Erro ao excluir brinde.'
    await proxy.$alert(msg, 'Erro')
  }
}
</script>

<style scoped>

:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

</style>
